<template>
  <v-menu
    v-model="showMenu"
    :close-on-content-click="false"
    :close-on-click="closeOnClick"
    max-width="690"
    min-width="681"
    left
    offset-y
  >
    <template v-if="noInput" v-slot:activator="{ on }">
      <div v-on="on">
        <v-icon
          color="info"
          v-text="$getIcon('mdiCalendarMonthOutline')"
        ></v-icon>
        <div
          v-if="filterChoices.includes(appliedFilter)"
          v-text="appliedFilter"
        ></div>
        <v-tooltip v-else bottom>
          <template v-slot:activator="{ on }">
            <div v-on="on" v-text="'Custom'"></div>
          </template>
          <span v-text="appliedFilter"></span>
        </v-tooltip>
      </div>
    </template>
    <template v-else v-slot:activator="{ on }">
      <v-text-field
        v-model="appliedFilter"
        :hint="hint"
        :disabled="loading"
        :persistent-hint="persistentHint"
        :hide-details="hideDetails"
        :error="errorMessages.length > 0"
        :error-messages="errorMessages"
        readonly
        :prepend-inner-icon="$getIcon('mdiMagnify')"
        :label="label"
        v-on="on"
        @paste.prevent
      ></v-text-field>
    </template>
    <v-card v-if="validCalendar && fetched" max-width="690" min-width="681">
      <vue-card-title
        :title="'Date Filter Menu'"
        :subtitle="''"
        class="primaryLight py-4"
        :icon="$getIcon('mdiCalendarQuestion')"
      >
        <template slot="actions">
          <v-switch
            v-model="international"
            class="ma-0 pa-0"
            hide-details
            label="English"
          ></v-switch>
        </template>
      </vue-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0 black--text">
        <v-col class="pa-0 d-flex horizontal-scrollbar">
          <v-list class="elevation-1 pa-0">
            <template v-for="(choice, index) in filterChoices">
              <v-list-item
                :key="index"
                :class="
                  appliedFilter === choice
                    ? international
                      ? 'blue accent-1'
                      : 'green lighten-2'
                    : ''
                "
                style="min-height: 36px !important;"
                @click="setDateAndFilter(choice)"
              >
                <v-list-item-title
                  :class="appliedFilter === choice ? 'white--text' : ''"
                  class="body-2"
                  v-text="choice"
                ></v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
          <v-divider vertical></v-divider>
          <localized-calendar
            v-if="!international"
            ref="child"
            v-model="startDate"
            :fiscal-year="fiscalYearList"
            :localized-date.sync="localizedStartDate"
            :disable-date="disableStartDate"
            @limit-mapping="limitMapping = $event"
          >
          </localized-calendar>
          <v-date-picker
            v-else
            v-model="startDate"
            no-title
            :allowed-dates="allowedDates"
          ></v-date-picker>
          <v-divider vertical></v-divider>
          <localized-calendar
            v-if="!international"
            v-model="endDate"
            :fiscal-year="fiscalYearList"
            :localized-date.sync="localizedEndDate"
            :disable-date="disableEndDate"
            @limit-mapping="limitMapping = $event"
          >
          </localized-calendar>
          <v-date-picker
            v-else
            v-model="endDate"
            no-title
            :allowed-dates="allowedDates"
          ></v-date-picker>
        </v-col>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="primaryLight">
        <v-text-field
          v-if="!international"
          v-model="localizedStartDate"
          readonly
          hide-details
          label="Start Date"
          class="mx-2"
        ></v-text-field>
        <v-text-field
          v-else
          v-model="startDate"
          readonly
          hide-details
          label="Start Date"
          class="mx-2"
        ></v-text-field>
        <v-text-field
          v-if="!international"
          v-model="localizedEndDate"
          readonly
          hide-details
          label="End Date"
          class="mx-2"
        ></v-text-field>
        <v-text-field
          v-else
          v-model="endDate"
          readonly
          hide-details
          label="End Date"
          class="mx-2"
        ></v-text-field>
        <v-btn
          :color="
            international
              ? 'blue accent-1 white--text'
              : 'green lighten-2 white--text'
          "
          class="mx-4"
          depressed
          small
          @click="applyFilter"
        >
          <span class="mx-2">Apply</span>
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else class="text-center py-2">
      <span v-if="fetched" class="danger--text"
        >Please set Fiscal Year first in Organization Settings.</span
      >
      <default-svg-loader
        v-else
        message="Please wait. Calendar is loading ..."
      ></default-svg-loader>
    </v-card>
  </v-menu>
</template>
<script>
import isEmpty from "lodash/isEmpty";
import LocalizedCalendar from "@/components/common/form_utils/LocalizedCalendar";
import DefaultSvgLoader from "@/components/common/layout_utils/DefaultSvgLoader";
import NepaliCalendarMixin from "@/mixins/NepaliCalendarMixin";
import { mapGetters } from "vuex";
export default {
  components: { LocalizedCalendar, DefaultSvgLoader },
  mixins: [NepaliCalendarMixin],
  props: {
    value: {
      type: Object,
      default: undefined
    },
    default: {
      type: String,
      default: "This Month"
    },
    includeFuture: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ""
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    noInput: {
      type: Boolean,
      default: false
    },
    excludeFilter: {
      type: Array,
      default() {
        return [];
      }
    },
    includeFilter: {
      type: Array,
      default() {
        return [];
      }
    },
    // For irregularity, filter must not be applicable beyond yesterday. Must exclude today's date.
    excludeToday: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default() {
        return [];
      }
    },
    disableStartDate: {
      type: Boolean,
      default: false
    },
    disableEndDate: {
      type: Boolean,
      default: false
    },
    supervisor: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: "Filter by Date"
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filterChoices: [
        "Today",
        "This Week",
        "This Month",
        "Last Month",
        // 'Fiscal Year',
        "This Year",
        "Till Date"
      ],
      international: false,
      fetched: false,
      validCalendar: true,
      fiscalYearList: [],
      limitMapping: {
        min: "",
        max: ""
      },
      appliedFilter: this.default,
      startDate: "",
      endDate: "",
      localizedStartDate: "",
      localizedEndDate: "",
      showMenu: false,
      closeOnClick: true,
      dateRangeMap: {
        "All Time": {
          startDate: "",
          endDate: ""
        },
        "Till Date": {
          startDate: "",
          endDate: this.$dayjs().format("YYYY-MM-DD")
        },
        Today: {
          startDate: this.$dayjs().format("YYYY-MM-DD"),
          endDate: this.$dayjs().format("YYYY-MM-DD")
        },
        Yesterday: {
          startDate: this.$dayjs()
            .subtract(1, "days")
            .format("YYYY-MM-DD"),
          endDate: this.$dayjs()
            .subtract(1, "days")
            .format("YYYY-MM-DD")
        },
        "This Month": {
          startDate: "",
          endDate: ""
        },
        "Last Month": {
          startDate: "",
          endDate: ""
        },
        // 'Fiscal Year': {
        //   startDate: '',
        //   endDate: ''
        // },
        "This Year": {
          startDate: "",
          endDate: ""
        },
        "Last Year": {
          startDate: "",
          endDate: ""
        },
        "This Week": {
          startDate: this.$dayjs()
            .startOf("week")
            .format("YYYY-MM-DD"),
          endDate: this.includeFuture
            ? this.$dayjs()
                .endOf("week")
                .format("YYYY-MM-DD")
            : this.excludeToday
            ? this.$dayjs()
                .subtract(1, "days")
                .format("YYYY-MM-DD")
            : this.$dayjs().format("YYYY-MM-DD")
        },
        "Last Week": {
          startDate: this.$dayjs()
            .startOf("week")
            .subtract(1, "week")
            .format("YYYY-MM-DD"),
          endDate: this.$dayjs()
            .startOf("week")
            .subtract(1, "days")
            .format("YYYY-MM-DD")
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      getSupervisorOrgSlug: "supervisor/getOrganizationSlug"
    }),
    changeOccurred() {
      if (
        this.value.start_date === undefined &&
        this.value.end_date === undefined &&
        this.default === this.appliedFilter
      ) {
        return false;
      } else {
        return !(
          this.value.start_date === this.startDate &&
          this.value.end_date === this.endDate
        );
      }
    }
  },
  watch: {
    value(val) {
      if (val.start_date !== this.startDate || val.end_date !== this.endDate) {
        this.updateDisplayValue(val);
      }
    },
    startDate(val) {
      this.updateDisplayValue({ start_date: val, end_date: this.endDate });
      this.closeOnClick = !this.changeOccurred;
    },
    endDate(val) {
      this.updateDisplayValue({ start_date: this.startDate, end_date: val });
      this.closeOnClick = !this.changeOccurred;
    },
    appliedFilter() {
      this.$emit("update", { dateStatus: this.appliedFilter });
    },
    international: {
      handler: function(val) {
        let today = this.$dayjs().format("YYYY-MM-DD");
        let yesterday = this.$dayjs()
          .subtract(1, "days")
          .format("YYYY-MM-DD");
        if (val) {
          this.dateRangeMap["This Month"].startDate = this.$dayjs()
            .startOf("month")
            .format("YYYY-MM-DD");
          this.dateRangeMap["This Month"].endDate = this.includeFuture
            ? this.$dayjs()
                .endOf("month")
                .format("YYYY-MM-DD")
            : this.excludeToday
            ? yesterday
            : today;
          this.dateRangeMap["Last Month"].startDate = this.$dayjs()
            .startOf("month")
            .subtract(1, "month")
            .format("YYYY-MM-DD");
          this.dateRangeMap["Last Month"].endDate = this.$dayjs()
            .startOf("month")
            .subtract(1, "days")
            .format("YYYY-MM-DD");
          this.dateRangeMap["This Year"].startDate = this.$dayjs()
            .startOf("year")
            .format("YYYY-MM-DD");
          this.dateRangeMap["This Year"].endDate = this.includeFuture
            ? this.$dayjs()
                .endOf("year")
                .format("YYYY-MM-DD")
            : this.excludeToday
            ? yesterday
            : today;
          this.dateRangeMap["Last Year"].startDate = this.$dayjs()
            .startOf("year")
            .subtract(1, "year")
            .format("YYYY-MM-DD");
          this.dateRangeMap["Last Year"].endDate = this.$dayjs()
            .startOf("year")
            .subtract(1, "days")
            .format("YYYY-MM-DD");
        } else {
          this.dateRangeMap["This Month"].startDate = this.getMonthStartDate();
          this.dateRangeMap["This Month"].endDate = this.includeFuture
            ? this.getMonthEndDate()
            : this.excludeToday
            ? yesterday
            : today;
          this.dateRangeMap[
            "Last Month"
          ].startDate = this.getLastMonthStartDate();
          this.dateRangeMap["Last Month"].endDate = this.getLastMonthEndDate();
          this.dateRangeMap[
            "This Year"
          ].startDate = this.getThisYearStartDate();
          this.dateRangeMap["This Year"].endDate = this.includeFuture
            ? this.getThisYearEndDate()
            : this.excludeToday
            ? yesterday
            : today;
          this.dateRangeMap[
            "Last Year"
          ].startDate = this.getLastYearStartDate();
          this.dateRangeMap["Last Year"].endDate = this.getLastYearEndDate();
        }
        if (this.dateRangeMap[this.appliedFilter]) {
          this.startDate = this.dateRangeMap[this.appliedFilter].startDate;
          this.endDate = this.dateRangeMap[this.appliedFilter].endDate;
        }
      },
      immediate: true
    }
  },
  async created() {
    if (this.excludeFilter.length > 0) {
      for (let i = 0; i < this.filterChoices.length; i++) {
        for (let j = 0; j < this.excludeFilter.length; j++) {
          if (this.filterChoices[i] === this.excludeFilter[j]) {
            this.filterChoices.splice(i, 1);
            i--;
          }
        }
      }
    }
    if (this.includeFilter.length > 0) {
      for (let i = 0; i < this.includeFilter.length; i++) {
        if (!this.filterChoices.includes(this.includeFilter[i])) {
          this.filterChoices.push(this.includeFilter[i]);
        }
      }
    }
    // The below commented code will provide the fiscal year to the localized calendar so as to render the specific date range as per the fiscal year
    // this.fetched = false
    // await this.$http.get(`/org/${this.supervisor ? this.getSupervisorOrgSlug : this.getOrganizationSlug}/fiscal-year/`)
    //   .then((response) => {
    //     if (response.results.length < 1) {
    //       this.validCalendar = false
    //       return
    //     }
    //     response.results.forEach(x => {
    //       if (x.applicable_from < this.$dayjs().format('YYYY-MM-DD') && x.applicable_to > this.$dayjs().format('YYYY-MM-DD')) {
    //         this.dateRangeMap['Fiscal Year'].startDate = x.applicable_from
    //         this.dateRangeMap['Fiscal Year'].endDate = x.applicable_to
    //       }
    //     })
    //     this.fiscalYearList = response.results
    this.fetched = true;
    //   })
    if (this.default === "All Time") {
      this.filterChoices.push("All Time");
    }
    if (
      !this.value ||
      isEmpty(this.value) ||
      (!this.value.start_date && !this.value.end_date)
    ) {
      this.createDateRange(this.appliedFilter);
    } else {
      this.updateDisplayValue(this.value);
    }
  },
  methods: {
    emitDateRange() {
      // if (!this.changeOccurred) return
      const payload = {
        start_date: this.startDate,
        end_date: this.endDate
      };
      this.$emit("input", payload);
    },
    applyFilter() {
      this.emitDateRange();
      this.setFilter();
      this.showMenu = false;
      this.closeOnClick = true;
    },
    setDateAndFilter(choice) {
      this.startDate = this.dateRangeMap[choice].startDate;
      this.endDate = this.dateRangeMap[choice].endDate;
      this.appliedFilter = choice;
    },
    createDateRange(choice) {
      this.setDateAndFilter(choice);
      this.emitDateRange();
      this.setFilter();
    },
    setFilter() {
      if (!this.filterChoices.includes(this.appliedFilter)) {
        this.appliedFilter =
          (this.startDate || "N/A") + "  -  " + (this.endDate || "N/A");
      }
    },
    updateDisplayValue(val) {
      this.startDate = val.start_date;
      this.endDate = val.end_date;
      for (const choice of this.filterChoices) {
        const filter = this.dateRangeMap[choice];
        if (
          filter.startDate === this.startDate &&
          filter.endDate === this.endDate
        ) {
          if (
            choice === "Today" &&
            this.appliedFilter === "This Month" &&
            this.getMonthStartDate() === this.$dayjs().format("YYYY-MM-DD")
          ) {
            return;
          }
          this.appliedFilter = choice;
          return;
        }
      }
      this.appliedFilter =
        (this.startDate || "N/A") + "  -  " + (this.endDate || "N/A");
    },
    allowedDates(val) {
      return val <= this.limitMapping.max && val >= this.limitMapping.min;
    }
  }
};
</script>
